'use client'

import type { FC } from 'react'
import styles from './CurrentLocationAddressPlaceholder.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import {
  EditIcon,
  ExclamationTriangleIcon,
  IconBlob,
  IconBlobType,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
  IconSize,
  ListItem,
  LocationArrowIcon,
  Tag,
  TagStyle,
  TagVariant,
} from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { addressLandingOptionClickedEvent } from '@/user/address-book/analytics'
import type { UserAddressNew } from '@/user/address-book/entities'

interface Props {
  currentAddress: UserAddressNew
}

export const CurrentLocationAddressPlaceholder: FC<Props> = ({ currentAddress }) => {
  const { t } = useLocalisation()
  const { setAddress, navigate, setAddressFormTrigger, setOriginalCoordinates } =
    useAddressBookContext()

  const address: UserAddressNew = {
    tag: null,
    addressLine: currentAddress.addressLine,
    latitude: currentAddress.latitude,
    longitude: currentAddress.longitude,
    cityCode: currentAddress.cityCode,
    cityName: currentAddress.cityName,
    countryCode: currentAddress.countryCode,
    fields: [],
    details: '',
    kind: 'APARTMENT',
  }

  const handleCreateAddressClick = () => {
    setAddress(address)
    setOriginalCoordinates(address)
    setAddressFormTrigger('UnsavedCurrentLocation')
    navigate('onCreateAddressFromCurrentLocation')
    addressLandingOptionClickedEvent('CurrentLocation')
  }

  const trailingContent = (
    <IconButton
      aria-label={t('address-list.edit-address-button')}
      icon={EditIcon}
      size={IconButtonSize.small}
      variant={IconButtonVariants.neutral}
      onClick={handleCreateAddressClick}
    />
  )
  return (
    <ListItem trailingContent={trailingContent} withDivider={true}>
      <div className={styles.addressCard} data-testid="current-location-placeholder">
        <div aria-label={t('address-list.current-location')} role="img">
          <IconBlob icon={LocationArrowIcon} size={IconSize.medium} type={IconBlobType.positive} />
        </div>

        <div className={styles.address}>
          <p>{address.addressLine}</p>
          <p className={styles.details}>{t('address-list.current-location')}</p>
          <Tag
            className={styles.tag}
            label={t('address-list.missing-details-tag')}
            leadingIcon={ExclamationTriangleIcon}
            style={TagStyle.warning}
            variant={TagVariant.secondary}
          />
        </div>
      </div>
    </ListItem>
  )
}
